import React, { useRef } from "react";
import { t } from "ttag";

import type {
    MultiBrowseBlock as ItemValue,
    MultiBrowseBlockWrapper as Value,
} from "@reactivated";

import { StreamField } from "@thelabnyc/thelabui/src/components/StreamField";
import { assertNever } from "@thelabnyc/thelabui/src/utils/never";

import { Themed } from "../../styles/themes";
import { Carousel } from "../Carousel/Carousel";
import { RichTextStreamBlock } from "../RichTextStreamBlock";
import { SKSArrows } from "../SKSCarousel/Arrows";
import { ScrollProgress } from "../SKSCarousel/ScrollProgress";
import { SpacerBlock } from "../SpacerBlock";
import { CaptionSlide, ProductSlide, TextSlide } from "./Slides";

import breakpoint from "../../styles/breakpoint.module.scss";
import styles from "./index.module.scss";

const slidesVisibleWithContent = 2.3;
const slidesVisibleFullWidth = 3.86;
const slidesVisibleTablet = 2.3;
const slidesVisibleMobile = 1.37;

const MultiBrowseBlock = ({ value }: { value: ItemValue }) => {
    const scrollRef = useRef<HTMLDivElement>(null);
    const { slides, content, block_label } = value;

    if (!slides || slides.length === 0) return null;

    const hasRichText = content?.content && content.content.length > 0;

    const slidesVisibleDesktop = hasRichText
        ? slidesVisibleWithContent
        : slidesVisibleFullWidth;

    // Subgrid needs a grid ancestor owning the row tracks. Switch to
    // productSlidesWrapper when all slides are products.
    const isProductCarousel = slides.every((slide) => slide.type === "product");

    const headingBlockId = content?.content?.find(
        (b) =>
            (b.type === "heading" || b.type === "eyebrow") &&
            b.value.tag !== "p",
    )?.id;

    const carouselLabelProps = headingBlockId
        ? { labelledBy: headingBlockId }
        : { label: block_label || t`Browse` };

    const carouselVwDesktop = hasRichText
        ? Math.round(
              (parseInt(styles.carouselColumnsWithContent) /
                  parseInt(styles.gridColumns)) *
                  100,
          )
        : 100;

    const imageSizes = [
        `(min-width: ${breakpoint.tablet}) ${Math.round(carouselVwDesktop / slidesVisibleDesktop)}vw`,
        `(min-width: ${breakpoint.mobile}) ${Math.round(100 / slidesVisibleTablet)}vw`,
        `${Math.round(100 / slidesVisibleMobile)}vw`,
    ].join(", ");

    return (
        <div
            className={
                hasRichText
                    ? styles.layoutWithRichText
                    : styles.layoutWithoutRichText
            }
            style={{
                "--slides-visible-desktop": slidesVisibleDesktop,
                "--slides-visible-tablet": slidesVisibleTablet,
                "--slides-visible-mobile": slidesVisibleMobile,
            }}
        >
            {hasRichText && (
                <RichTextStreamBlock
                    value={content}
                    className={styles.richText}
                />
            )}
            <div className={styles.carouselArea}>
                <Carousel
                    {...carouselLabelProps}
                    loop={false}
                    scrollContainerProps={{
                        className: styles.scrollContainer,
                        ref: scrollRef,
                    }}
                    slideWrapperProps={{
                        className: isProductCarousel
                            ? styles.productSlidesWrapper
                            : styles.slidesWrapper,
                    }}
                    slideContainerProps={{
                        className: isProductCarousel
                            ? styles.productSlideItem
                            : styles.slideItem,
                    }}
                    controls={({ allVisible }) => (
                        <SKSArrows
                            allVisible={allVisible}
                            className={styles.arrows}
                        />
                    )}
                    dots={null}
                >
                    {slides.map((slide) => {
                        switch (slide.type) {
                            case "caption":
                                return (
                                    <CaptionSlide
                                        key={slide.id}
                                        value={slide.value}
                                        imageSizes={imageSizes}
                                    />
                                );
                            case "text":
                            case "facet_value":
                                return (
                                    <TextSlide
                                        key={slide.id}
                                        value={slide.value}
                                        imageSizes={imageSizes}
                                    />
                                );
                            case "product":
                                return (
                                    <ProductSlide
                                        key={slide.id}
                                        value={slide.value}
                                    />
                                );
                        }
                        return assertNever(slide);
                    })}
                </Carousel>
            </div>
            <ScrollProgress
                scrollRef={scrollRef}
                className={styles.progressBar}
            />
        </div>
    );
};

const components = {
    multi_browse: MultiBrowseBlock,
    spacer: SpacerBlock,
};

export const MultiBrowseBlockWrapper = ({ value }: { value: Value }) => {
    if (!value.items) return null;
    return (
        <Themed theme={value.theme} tag="section">
            <StreamField components={components} value={value.items} />
        </Themed>
    );
};
